import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Mail, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { useAuth, ScrollArea } from '@aaas/shared';
import { useEmailThreadListQuery, useEmailThreadQuery } from '../hooks/useEmailQueries';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { sanitizeHtml } from '../utils/sanitize-html';
import type { EmailThread, EmailMessage } from '../types';

interface EmailThreadPanelProps {
  cvId: string;
}

export function EmailThreadPanel({ cvId }: EmailThreadPanelProps) {
  const { t } = useTranslation('ats');
  const { currentWorkspace, idToken } = useAuth();
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);

  const { data: threadResponse, isLoading } = useEmailThreadListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    cvId,
    enabled: !!currentWorkspace?.id && !!idToken,
  });

  const threads = threadResponse?.data?.items || [];

  if (isLoading) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        {t('email.threadPanel.loading')}
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-gray-400 flex flex-col items-center gap-1">
        <Mail className="h-5 w-5" />
        <span>{t('email.threadPanel.empty')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {threads.map((thread) => (
        <ThreadItem
          key={thread.id}
          thread={thread}
          isExpanded={expandedThreadId === thread.id}
          onToggle={() =>
            setExpandedThreadId(expandedThreadId === thread.id ? null : thread.id)
          }
        />
      ))}
    </div>
  );
}

function ThreadItem({
  thread,
  isExpanded,
  onToggle,
}: {
  thread: EmailThread;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('ats');
  const { currentWorkspace, idToken } = useAuth();

  const { data: threadDetail } = useEmailThreadQuery({
    threadId: isExpanded ? thread.id : undefined,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: isExpanded,
  });

  const messages = threadDetail?.data?.messages || [];

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{thread.subject}</p>
          <p className="text-xs text-gray-500">
            {thread.last_message_at
              ? formatDistanceToNow(new Date(thread.last_message_at), {
                  addSuffix: true,
                  locale: vi,
                })
              : '—'}
            {thread.job_code && (
              <span className="ml-2 text-gray-400">{t('email.threadPanel.jobLabel', { code: thread.job_code })}</span>
            )}
          </p>
        </div>
      </button>

      {isExpanded && (
        <ScrollArea className="max-h-80">
          <div className="px-3 pb-3 space-y-2">
            {messages.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">{t('email.threadPanel.loadingMessages')}</p>
            ) : (
              messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: EmailMessage }) {
  const { t } = useTranslation('ats');
  const isOutbound = message.direction === 'outbound';

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isOutbound
            ? 'bg-orange-50 border border-orange-100'
            : 'bg-gray-50 border border-gray-200'
        }`}
      >
        <div className="flex items-center gap-1.5 mb-1">
          {isOutbound ? (
            <ArrowUpRight className="h-3 w-3 text-orange-500" />
          ) : (
            <ArrowDownLeft className="h-3 w-3 text-blue-500" />
          )}
          <span className="text-xs font-medium text-gray-600">
            {message.from_email}
          </span>
          <span className="text-xs text-gray-400">
            {formatDistanceToNow(new Date(message.sent_at ?? message.created_at), {
              addSuffix: true,
              locale: vi,
            })}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-1">{message.subject}</p>
        {message.body_html ? (
          <div
            className="text-sm prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.body_html) }}
          />
        ) : message.body_text ? (
          <p className="text-sm whitespace-pre-wrap">{message.body_text}</p>
        ) : null}
        {message.status === 'failed' && (
          <span className="inline-flex items-center text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded mt-1">
            {t('email.detail.badge.failed')}
          </span>
        )}
      </div>
    </div>
  );
}
